import { motion } from "framer-motion";
import { useState } from "react";
import { Send } from "lucide-react";
import { DUR, EASE, STAGGER, VIEWPORT } from "../lib/motion";
import TextReveal from "../components/TextReveal/TextReveal";
import ContactScene from "../components/Morph/scenes/ContactScene";
import { usePhoneScene, useStoryMode } from "../hooks/useStoryMode";
import { INFO_ITEMS as infoItems, SOCIAL_LINKS as socialLinks } from "../data/contact";
import { sendMessage } from "../lib/sendMessage";
import "./Contact.css";

export default function Contact() {
    const [formData, setFormData] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState("");
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(false);

    /** Resolves true once the message is actually delivered. */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<boolean> => {
        e.preventDefault();
        if (sending) return false;

        // Honeypot: a box real visitors never see. The server answers a ticked
        // one with a quiet "sent" and emails nothing.
        const bot = !!(e.currentTarget.elements.namedItem("botcheck") as HTMLInputElement | null)?.checked;

        setSending(true);
        setFailed(false);
        setStatus("");
        const result = await sendMessage(formData, bot);
        setSending(false);

        if (result.ok) {
            setStatus("Message sent! I'll get back to you soon.");
            setTimeout(() => {
                setFormData({ name: "", email: "", message: "" });
                setStatus("");
            }, 3500);
            return true;
        }
        setFailed(true);
        setStatus(result.message);
        setTimeout(() => {
            setStatus("");
            setFailed(false);
        }, 6000);
        return false;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const story = useStoryMode();
    const phone = usePhoneScene();
    if (story || phone) {
        return (
            <ContactScene
                phone={!story}
                formData={formData}
                status={status}
                sending={sending}
                failed={failed}
                onChange={handleChange}
                onSubmit={handleSubmit}
            />
        );
    }

    return (
        <div className="contact-page">
            <div className="contact-container">
                <div className="contact-header">
                    <TextReveal as="h1" className="contact-title" mutedFrom={2}>
                        Get In Touch
                    </TextReveal>
                    <TextReveal as="p" className="contact-subtitle" delay={0.14}>
                        {"Let's work together on your next project"}
                    </TextReveal>
                </div>

                <div className="contact-content">
                    <motion.div
                        className="contact-form-wrapper"
                        initial={{ x: -24, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={VIEWPORT}
                        transition={{ duration: DUR.base, delay: STAGGER * 2, ease: EASE }}
                    >
                        <form onSubmit={handleSubmit} className="contact-form">
                            <motion.div
                                className="form-group"
                                initial={{ opacity: 0, y: -18, scaleY: 0.4 }}
                                whileInView={{ opacity: 1, y: 0, scaleY: 1 }}
                                viewport={VIEWPORT}
                                transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.10 }}
                                style={{ transformOrigin: "50% 0" }}
                            >
                                <label htmlFor="name">Your Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="John Doe"
                                />
                            </motion.div>

                            <motion.div
                                className="form-group"
                                initial={{ opacity: 0, y: -18, scaleY: 0.4 }}
                                whileInView={{ opacity: 1, y: 0, scaleY: 1 }}
                                viewport={VIEWPORT}
                                transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.19 }}
                                style={{ transformOrigin: "50% 0" }}
                            >
                                <label htmlFor="email">Your Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="john@example.com"
                                />
                            </motion.div>

                            <motion.div
                                className="form-group"
                                initial={{ opacity: 0, y: -18, scaleY: 0.4 }}
                                whileInView={{ opacity: 1, y: 0, scaleY: 1 }}
                                viewport={VIEWPORT}
                                transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.28 }}
                                style={{ transformOrigin: "50% 0" }}
                            >
                                <label htmlFor="message">Your Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows={5}
                                    placeholder="Tell me about your project..."
                                />
                            </motion.div>

                            <input
                                type="checkbox"
                                name="botcheck"
                                className="contact-botcheck"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                            />

                            <motion.button
                                type="submit"
                                className="submit-btn"
                                disabled={sending}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span>{sending ? "Sending…" : "Send Message"}</span>
                                <Send size={16} strokeWidth={2} />
                                <span className="btn-beat" aria-hidden="true" />
                            </motion.button>

                            {status && (
                                <motion.div
                                    className={`status-message${failed ? " is-error" : ""}`}
                                    role="status"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {status}
                                </motion.div>
                            )}
                        </form>
                    </motion.div>

                    <motion.div
                        className="contact-info"
                        initial={{ x: 24, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={VIEWPORT}
                        transition={{ duration: DUR.base, delay: STAGGER * 4, ease: EASE }}
                    >
                        <div className="info-card">
                            <h3 className="info-title">Contact Information</h3>
                            <div className="info-items">
                                {infoItems.map(({ label, value, Icon }) => (
                                    <div key={label} className="info-item">
                                        <span className="info-icon">
                                            <Icon size={16} strokeWidth={1.8} />
                                        </span>
                                        <div>
                                            <p className="info-label">{label}</p>
                                            <p className="info-value">{value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="social-links">
                            <h3 className="social-title">Connect With Me</h3>
                            <div className="social-grid">
                                {socialLinks.map(({ name, url, Icon }, index) => (
                                    <motion.a
                                        key={name}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="social-card"
                                        initial={{ y: 12, opacity: 0 }}
                                        whileInView={{ y: 0, opacity: 1 }}
                                        viewport={VIEWPORT}
                                        transition={{
                                            duration: DUR.quick,
                                            delay: STAGGER * (6 + index),
                                            ease: EASE,
                                        }}
                                        whileHover={{ y: -3 }}
                                        whileTap={{ scale: 0.97 }}
                                    >
                                        <span className="social-icon">
                                            <Icon size={18} />
                                        </span>
                                        <span className="social-name">{name}</span>
                                    </motion.a>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
